var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// ── Browser storage key constants ─────────────────────────────────────────
import { GIS_TOKEN_EXPIRATION } from '@app/modules/gis/gis-config';
export var GIS_ACCESS_TOKEN_KEY = 'geobrain_gis_access_token';
export var GIS_TOKEN_EXPIRY_KEY = 'geobrain_gis_token_expiry';
export var GIS_REFRESH_TOKEN_KEY = 'geobrain_gis_refresh_token';
export var GIS_REFRESH_TOKEN_EXPIRY_KEY = 'geobrain_gis_refresh_token_expiry';
export var GIS_PORTAL_URL_KEY = 'geobrain_gis_portal_url';
export var GIS_PORTAL_APPID_KEY = 'geobrain_gis_portal_appid';
// Keys written before a PKCE redirect — read and cleared by main.ts on return.
var GIS_VERIFIER_KEY = 'geobrain_gis_code_verifier';
var GIS_PKCE_APPID_KEY = 'geobrain_gis_app_id'; // main.ts reads this key
var GIS_RETURN_URL_KEY = 'geobrain_gis_return_url';
// Deduplicates concurrent refresh attempts — all callers await the same promise.
var _refreshPromise = null;
// ── Public API ──────────────────────────────────────────────────────────────
/**
 * Returns true if a non-expired GIS access token exists in sessionStorage.
 * Clears stale token entries when expired.
 */
export function hasValidGisToken() {
    var token = sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY);
    var expiry = parseInt(sessionStorage.getItem(GIS_TOKEN_EXPIRY_KEY) || '0', 10);
    if (token && expiry && Date.now() < expiry) {
        return true;
    }
    sessionStorage.removeItem(GIS_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(GIS_TOKEN_EXPIRY_KEY);
    return false;
}
/**
 * Returns a valid GIS access token.
 * If the current token is expired, attempts a silent refresh first.
 * If refresh also fails (refresh token gone/expired), restarts the full
 * PKCE auth flow — redirecting the user back to the portal so they can
 * re-authenticate mid-session without needing a route navigation.
 * Returns an empty string only if the portal config is not yet stored
 * (i.e. the guard hasn't run yet — it will handle first-time auth itself).
 */
export function getGisToken() {
    return __awaiter(this, void 0, void 0, function () {
        var refreshed;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (hasValidGisToken()) {
                        return [2 /*return*/, sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY)];
                    }
                    return [4 /*yield*/, refreshGisToken()];
                case 1:
                    refreshed = _a.sent();
                    if (!!refreshed) return [3 /*break*/, 3];
                    return [4 /*yield*/, startGisPkceFlow()];
                case 2:
                    _a.sent();
                    return [2 /*return*/, ''];
                case 3: return [2 /*return*/, sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY) || ''];
            }
        });
    });
}
/**
 * Starts a full PKCE authorization redirect.
 * Reads the portal URL and appId from sessionStorage (stored by storeGisPortalConfig).
 * If either is missing the guard has not yet run — returns without redirecting
 * so the guard can handle first-time auth on the next route activation.
 * Saves the current path as the post-auth return URL.
 */
export function startGisPkceFlow() {
    return __awaiter(this, void 0, void 0, function () {
        var portalUrl, appId, codeVerifier, codeChallenge, params;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    portalUrl = sessionStorage.getItem(GIS_PORTAL_URL_KEY);
                    appId = sessionStorage.getItem(GIS_PORTAL_APPID_KEY);
                    if (!portalUrl || !appId) {
                        return [2 /*return*/];
                    }
                    codeVerifier = generateCodeVerifier();
                    return [4 /*yield*/, generateCodeChallenge(codeVerifier)];
                case 1:
                    codeChallenge = _a.sent();
                    sessionStorage.setItem(GIS_VERIFIER_KEY, codeVerifier);
                    sessionStorage.setItem(GIS_PKCE_APPID_KEY, appId);
                    sessionStorage.setItem(GIS_RETURN_URL_KEY, window.location.pathname + window.location.search + window.location.hash);
                    params = new URLSearchParams({
                        client_id: appId,
                        response_type: 'code',
                        redirect_uri: window.location.origin + '/',
                        code_challenge: codeChallenge,
                        code_challenge_method: 'S256',
                        expiration: String(GIS_TOKEN_EXPIRATION)
                    });
                    window.location.href = portalUrl + "/sharing/oauth2/authorize?" + params;
                    return [2 /*return*/];
            }
        });
    });
}
function generateCodeVerifier() {
    var array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
}
function generateCodeChallenge(verifier) {
    return __awaiter(this, void 0, void 0, function () {
        var data, sha256, str;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    data = new TextEncoder().encode(verifier);
                    return [4 /*yield*/, crypto.subtle.digest('SHA-256', data)];
                case 1:
                    sha256 = _a.sent();
                    str = '';
                    new Uint8Array(sha256).forEach(function (b) { return str += String.fromCharCode(b); });
                    return [2 /*return*/, btoa(str)
                            .replace(/\+/g, '-')
                            .replace(/\//g, '_')
                            .replace(/=+$/, '')];
            }
        });
    });
}
/**
 * Synchronously returns whatever GIS token is currently in sessionStorage.
 * Use only for synchronous Leaflet layer construction (createDynamicMapLayer,
 * createFeatureLayer) where awaiting is not possible.  The guard ensures a
 * valid token is present before any map route loads.
 */
export function getGisTokenSync() {
    if (hasValidGisToken()) {
        return sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY);
    }
    // Token expired: trigger a silent background refresh so the new token is
    // stored in sessionStorage by the time the next sync call is made.
    // The current call returns '' — the Leaflet layer will retry on its next tick.
    refreshGisToken();
    return '';
}
/**
 * Attempts a silent token refresh using the stored refresh token.
 * Uses fetchWithRetry for automatic exponential backoff on transient errors
 * (network failures, 5xx, 429). Only permanent auth failures clear the token.
 * Returns true if a new access token was successfully obtained.
 * Concurrent calls share a single in-flight request.
 */
export function refreshGisToken() {
    var _this = this;
    if (_refreshPromise) {
        return _refreshPromise;
    }
    _refreshPromise = (function () { return __awaiter(_this, void 0, void 0, function () {
        var refreshOutcome, refreshToken, portalUrl, appId, rtExpiry, body, res, data, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    refreshOutcome = 'unknown';
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, 5, 6]);
                    refreshToken = sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY);
                    portalUrl = sessionStorage.getItem(GIS_PORTAL_URL_KEY);
                    appId = sessionStorage.getItem(GIS_PORTAL_APPID_KEY);
                    rtExpiry = parseInt(sessionStorage.getItem(GIS_REFRESH_TOKEN_EXPIRY_KEY) || '0', 10);
                    if (!refreshToken || !portalUrl || !appId) {
                        refreshOutcome = 'missing_refresh_context';
                        return [2 /*return*/, false];
                    }
                    if (rtExpiry && Date.now() >= rtExpiry) {
                        sessionStorage.removeItem(GIS_REFRESH_TOKEN_KEY);
                        sessionStorage.removeItem(GIS_REFRESH_TOKEN_EXPIRY_KEY);
                        refreshOutcome = 'refresh_token_expired';
                        return [2 /*return*/, false];
                    }
                    body = new URLSearchParams({
                        client_id: appId,
                        refresh_token: refreshToken,
                        grant_type: 'refresh_token',
                        redirect_uri: window.location.origin + '/'
                    });
                    return [4 /*yield*/, fetchWithRetry(portalUrl + "/sharing/oauth2/token?f=json", {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: body.toString()
                        })];
                case 2:
                    res = _a.sent();
                    // Transient upstream failures: keep refresh token for later retry.
                    if (res.status === 429 || res.status >= 500) {
                        refreshOutcome = "transient_http_" + res.status;
                        return [2 /*return*/, false];
                    }
                    // Permanent auth/client failures: refresh token is no longer usable.
                    if (res.status >= 400 && res.status < 500) {
                        if (hasValidGisToken()) {
                            refreshOutcome = "client_http_" + res.status + "_fallback_token_present";
                            return [2 /*return*/, true];
                        }
                        sessionStorage.removeItem(GIS_REFRESH_TOKEN_KEY);
                        sessionStorage.removeItem(GIS_REFRESH_TOKEN_EXPIRY_KEY);
                        refreshOutcome = "client_http_" + res.status;
                        return [2 /*return*/, false];
                    }
                    return [4 /*yield*/, res.json()];
                case 3:
                    data = _a.sent();
                    if (data.access_token) {
                        sessionStorage.setItem(GIS_ACCESS_TOKEN_KEY, data.access_token);
                        sessionStorage.setItem(GIS_TOKEN_EXPIRY_KEY, (Date.now() + data.expires_in * 1000).toString());
                        if (data.refresh_token) {
                            sessionStorage.setItem(GIS_REFRESH_TOKEN_KEY, data.refresh_token);
                            if (data.refresh_token_expires_in) {
                                sessionStorage.setItem(GIS_REFRESH_TOKEN_EXPIRY_KEY, (Date.now() + data.refresh_token_expires_in * 1000).toString());
                            }
                        }
                        refreshOutcome = 'refreshed_access_token';
                        return [2 /*return*/, true];
                    }
                    // Any error response from the portal means the refresh token is no longer usable.
                    // A concurrent tab may have already rotated it and stored a fresh access token.
                    if (hasValidGisToken()) {
                        refreshOutcome = 'portal_error_but_existing_token_present';
                        return [2 /*return*/, true];
                    }
                    refreshOutcome = 'portal_response_missing_access_token';
                    return [2 /*return*/, false];
                case 4:
                    err_1 = _a.sent();
                    console.error('refreshGisToken: unexpected error while refreshing GIS token', {
                        error: err_1,
                        hasAccessToken: !!sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY),
                        hasRefreshToken: !!sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY),
                        portalUrl: sessionStorage.getItem(GIS_PORTAL_URL_KEY),
                        appId: sessionStorage.getItem(GIS_PORTAL_APPID_KEY)
                    });
                    refreshOutcome = 'exception';
                    return [2 /*return*/, false];
                case 5:
                    console.debug('refreshGisToken: finished', {
                        outcome: refreshOutcome,
                        hasValidToken: hasValidGisToken(),
                        hasRefreshToken: !!sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY)
                    });
                    _refreshPromise = null;
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); })();
    return _refreshPromise;
}
/**
 * Persists the portal config needed for silent refresh and mid-session
 * PKCE restarts (called by GisAuthGuard on every canActivate).
 */
export function storeGisPortalConfig(portalUrl, appId) {
    sessionStorage.setItem(GIS_PORTAL_URL_KEY, portalUrl);
    sessionStorage.setItem(GIS_PORTAL_APPID_KEY, appId);
}
// ── Transient Error Retry Logic ───────────────────────────────────────────
/**
 * Retries a fetch operation with exponential backoff for transient errors only.
 * Makes up to 3 attempts with exponential delays between retries: 1s, 2s, 4s.
 *
 * Only retries on transient failures:
 *  - Network errors (fetch throws)
 *  - 5xx server errors
 *
 * Does NOT retry on 4xx client errors (they're permanent).
 */
export function fetchWithRetry(url, options) {
    return __awaiter(this, void 0, void 0, function () {
        var lastError, maxAttempts, _loop_1, attempt, state_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    lastError = null;
                    maxAttempts = 3;
                    _loop_1 = function (attempt) {
                        var res, delayMs_1, err_2, delayMs_2;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    _a.trys.push([0, 5, , 8]);
                                    return [4 /*yield*/, fetch(url, options)];
                                case 1:
                                    res = _a.sent();
                                    // Don't retry 4xx errors (they're permanent)
                                    if (res.status >= 400 && res.status < 500) {
                                        return [2 /*return*/, { value: res }];
                                    }
                                    if (!(res.status >= 500)) return [3 /*break*/, 4];
                                    lastError = new Error("Server error: " + res.status);
                                    if (!(attempt < maxAttempts)) return [3 /*break*/, 3];
                                    delayMs_1 = Math.pow(2, attempt - 1) * 1000;
                                    return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, delayMs_1); })];
                                case 2:
                                    _a.sent();
                                    return [2 /*return*/, "continue"];
                                case 3: return [2 /*return*/, { value: res }];
                                case 4: return [2 /*return*/, { value: res }];
                                case 5:
                                    err_2 = _a.sent();
                                    // Network error — retry with backoff
                                    lastError = err_2;
                                    if (!(attempt < maxAttempts)) return [3 /*break*/, 7];
                                    delayMs_2 = Math.pow(2, attempt - 1) * 1000;
                                    return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, delayMs_2); })];
                                case 6:
                                    _a.sent();
                                    return [2 /*return*/, "continue"];
                                case 7: throw err_2;
                                case 8: return [2 /*return*/];
                            }
                        });
                    };
                    attempt = 1;
                    _a.label = 1;
                case 1:
                    if (!(attempt <= maxAttempts)) return [3 /*break*/, 4];
                    return [5 /*yield**/, _loop_1(attempt)];
                case 2:
                    state_1 = _a.sent();
                    if (typeof state_1 === "object")
                        return [2 /*return*/, state_1.value];
                    _a.label = 3;
                case 3:
                    attempt++;
                    return [3 /*break*/, 1];
                case 4: 
                // Should not reach here, but throw last error if we do
                throw lastError || new Error('Fetch with retry failed');
            }
        });
    });
}
// ── Auth Retry Counter (sessionStorage, per-tab) ────────────────────────────
var GIS_AUTH_RETRY_COUNT_KEY = 'geobrain_gis_auth_retry_count';
/**
 * Increments the auth retry counter and returns the new value.
 * Used to track consecutive auth failures and eventually redirect to error page.
 */
export function incrementGisAuthRetryCounter() {
    var current = parseInt(sessionStorage.getItem(GIS_AUTH_RETRY_COUNT_KEY) || '0', 10);
    var next = current + 1;
    sessionStorage.setItem(GIS_AUTH_RETRY_COUNT_KEY, String(next));
    return next;
}
/**
 * Gets the current auth retry counter value (0 if never attempted).
 */
export function getGisAuthRetryCount() {
    return parseInt(sessionStorage.getItem(GIS_AUTH_RETRY_COUNT_KEY) || '0', 10);
}
/**
 * Clears the auth retry counter (called on successful auth or user retry).
 */
export function clearGisAuthRetryCounter() {
    sessionStorage.removeItem(GIS_AUTH_RETRY_COUNT_KEY);
}
